import { useEffect, useRef, useState } from "react";
import { Compass, Send, AlertCircle, Sparkles } from "lucide-react";
import { ApiService } from "../../services/api";
import type { CoachChatMessage, CoachChatResponse } from "../../types/navigator";

// Tanışma diyaloğu — İKİNCİL yol (sahibin kararı, 23 Eyl 2026: varsayılan tanışma form-önce sihirbaz,
// CoachOnboarding.tsx). Yazarak tanışmak isteyen öğrenci buraya gelir; koç mesaj başına tek soru sorar.

interface Props {
  onReady: () => void;
  onSwitchToForm?: () => void;
}

export default function CoachOnboardingChat({ onReady, onSwitchToForm }: Props) {
  const [messages, setMessages] = useState<CoachChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [creating, setCreating] = useState(false);
  const [bolum, setBolum] = useState<CoachChatResponse["bolum"]>(null);
  const [expect, setExpect] = useState<CoachChatResponse["expect"]>(null);
  const [lessons, setLessons] = useState<{ id: string; title: string }[]>([]);
  const [beyan, setBeyan] = useState<Record<string, string>>({});
  const bottomRef = useRef<HTMLDivElement>(null);

  const scrollDown = () => bottomRef.current?.scrollIntoView({ behavior: "smooth" });

  // Sunucu sohbeti son 20 mesajla kuruyor; ekran biriken TÜM geçmişi gönderince 20'yi geçen sohbet
  // kalıcı olarak 400 alıyordu ("messages must contain less than or equal to 20 items" — saha, 24 Eyl).
  const SEND_WINDOW = 20;

  const send = async (history: CoachChatMessage[]) => {
    setSending(true);
    setError(null);
    try {
      const res: CoachChatResponse = await ApiService.coachChat(history.slice(-SEND_WINDOW));
      setMessages([...history, { role: "assistant", content: res.reply }]);
      setReady(res.ready);
      if (res.bolum) setBolum(res.bolum);
      setExpect(res.expect ?? null);
      if (Array.isArray(res.lessons)) setLessons(res.lessons);
    } catch (e: any) {
      setError(e?.message || "Koç şu an yanıt veremiyor");
      // Gönderilen mesaj kaybolmasın: kutuya geri koy, öğrenci tekrar gönderebilsin
      const lastUser = [...history].reverse().find((m) => m.role === "user");
      if (lastUser) {
        setInput(lastUser.content);
        setMessages(history.slice(0, -1));
      }
    } finally {
      setSending(false);
      setTimeout(scrollDown, 50);
    }
  };

  // İlk açılış: yarıda kalmış tanışma varsa KALDIĞI YERDEN devam (sohbet artık profile
  // kaydediliyor — sayfadan çıkan öğrencinin konuşması kaybolmuyor); yoksa koç selam verir.
  useEffect(() => {
    (async () => {
      try {
        const res = await ApiService.getCoachProfile();
        const saved = res.profile?.metadata?.onboardingMessages;
        if (Array.isArray(saved) && saved.length) {
          setMessages(saved);
          setReady(res.ready === true);
          const b = res.profile?.metadata?.tanisma?.bolum;
          if (b) setBolum({ ad: b.ad, puanTuru: b.puanTuru, merdiven: b.merdiven, hedefSiralama: res.profile?.hedefSiralama || null });
          setTimeout(scrollDown, 50);
          return;
        }
      } catch {
        /* profil okunamazsa normal akışla başla */
      }
      send([]);
    })();
    // eslint-disable-next-line
  }, []);

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    const next: CoachChatMessage[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setTimeout(scrollDown, 50);
    await send(next);
  };

  // Hızlı seçim çipleri: sohbeti bozmaz, seçim düz cümle olarak gönderilir (model + kural çıkarımı okur)
  const sendText = async (text: string) => {
    if (!text || sending) return;
    const next: CoachChatMessage[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setTimeout(scrollDown, 50);
    await send(next);
  };
  const LEVELS: Array<[string, string]> = [["iyiyim", "iyi"], ["orta", "orta"], ["kotuyum", "kötü"], ["hic", "hiç"]];
  const shortTitle = (t: string) => t.replace(/^(TYT|AYT|LGS|KPSS|DGS|ALES)\s*-?\s*/i, "").replace(/\s*\(.*?\)\s*/g, "").trim();
  const submitBeyan = () => {
    const parts = lessons.filter((l) => beyan[l.id]).map((l) => `${shortTitle(l.title)} ${LEVELS.find(([k]) => k === beyan[l.id])?.[1] || beyan[l.id]}`);
    if (parts.length) sendText(parts.join(", "));
  };

  const buildPlan = async () => {
    setCreating(true);
    setError(null);
    try {
      await ApiService.createCoachPlan();
      onReady();
    } catch (e: any) {
      setError(e?.message || "Plan oluşturulamadı");
      setCreating(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col px-4 py-5 sm:px-6">
      <div className="mb-4 flex items-center gap-3">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-blue-100 text-blue-600">
          <Compass className="h-6 w-6" />
        </div>
        <div>
          <p className="text-base font-semibold text-gray-900">Koçunla tanış</p>
          <p className="text-xs text-gray-500">Önce seni tanıyalım: ne okumak istiyorsun, hangi dersleri seviyorsun. Program, iki haftalık ölçümden sonra.</p>
        </div>
      </div>
      {onSwitchToForm && (
        <button type="button" onClick={onSwitchToForm} className="mb-3 self-start text-xs font-medium text-blue-700 hover:underline">
          ← Seçeneklerle tanışmaya dön
        </button>
      )}

      {bolum && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900">
          <span className="font-semibold">{bolum.ad}</span>
          {bolum.puanTuru && <span className="rounded bg-white px-1.5 py-0.5">{bolum.puanTuru}</span>}
          {bolum.merdiven?.devletOrta && <span>devlet ortası {bolum.merdiven.devletOrta.toLocaleString("tr-TR")}</span>}
          {bolum.merdiven?.enIyi && <span>· en iyi {bolum.merdiven.enIyi.toLocaleString("tr-TR")}</span>}
          {bolum.hedefSiralama && <span className="ml-auto font-semibold">hedef {bolum.hedefSiralama.toLocaleString("tr-TR")}</span>}
        </div>
      )}
      <div className="mb-3 min-h-[280px] space-y-3 rounded-lg border border-gray-200 bg-white p-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                m.role === "user"
                  ? "bg-blue-600 text-white"
                  : "border border-gray-200 bg-gray-50 text-gray-800"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="h-3.5 w-3.5 animate-spin rounded-full border-b-2 border-blue-600" />
              <span className="text-xs text-gray-500">koç yazıyor…</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {error && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {!ready && !sending && expect === "band" && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {[["Devlet ortası olsun", "devlet ortası"], ["En iyisini hedefliyorum", "en iyisi"], ["Geniş tutalım, vakıf da olur", "geniş tutalım, vakıf da olur"]].map(([label, text]) => (
            <button key={text} type="button" onClick={() => sendText(text)} className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-800 hover:bg-blue-100">
              {label}
            </button>
          ))}
        </div>
      )}
      {!ready && !sending && expect === "beyan" && lessons.length > 0 && (
        <div className="mb-2 rounded-lg border border-gray-200 bg-white p-3">
          <p className="mb-2 text-xs text-gray-600">Ders ders seç (ya da aşağıya yaz):</p>
          <div className="space-y-1.5">
            {lessons.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center gap-1.5">
                <span className="w-32 truncate text-sm text-gray-800">{shortTitle(l.title)}</span>
                {LEVELS.map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setBeyan({ ...beyan, [l.id]: key })}
                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${beyan[l.id] === key ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ))}
          </div>
          <button type="button" onClick={submitBeyan} disabled={!lessons.some((l) => beyan[l.id])} className="mt-2 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            Gönder
          </button>
        </div>
      )}
      {ready ? (
        <button
          onClick={buildPlan}
          disabled={creating}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
        >
          {creating ? (
            <>
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
              Ölçüm hazırlanıyor…
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" /> Ölçüme başla
            </>
          )}
        </button>
      ) : (
        <form onSubmit={submit} className="flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={sending}
            placeholder="Cevabını yaz…"
            className="min-w-0 flex-1 rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none transition-colors focus:border-blue-400"
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:opacity-40"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      )}
    </div>
  );
}
